/**
 * Everything on the maintenance page that differs between the ten apps.
 *
 * The Figma frames share a layout, but NOT as much as first assumed. Verified
 * deviations, all from node geometry across the 20 frames:
 *
 *  - Logo aspect ratios run from 3.33:1 (Jubilee) to 6.32:1 (AI Dropship)
 *    against AliDrop's 4.26:1, so the logo box cannot be one fixed size.
 *  - Spocket and DropshipTool place the background rings 109px further right
 *    than the other eight.
 *  - The divider and card border are the app's own headline ink at low alpha,
 *    not a shared neutral — that only looked neutral because AliDrop's ink is
 *    #191919, i.e. rgb(25,25,25).
 *  - Spocket's pill border is 14%, not the 27% the other checked frames use.
 *
 * Copying this component to a sibling repo means editing this file and nothing
 * else. Values live in plans/maintenance-pages-all-apps/src/tokens.json.
 */

export type Promo = {
  name: string;
  headline: string;
  blurb: string;
  linkLabel: string;
  linkUrl: string;
};

export const DROPSHIPTOOL: Promo = {
  name: "Dropshiptool",
  headline: "Discover Winning Products and High-Performing Viral Ads",
  blurb:
    "Track over 3 million Shopify stores to uncover winning products, viral TikTok ads, and top-performing Facebook and Instagram creatives.",
  linkLabel: "dropshiptool.io",
  linkUrl: "https://dropshiptool.com/",
};

/** Used only on DropshipTool's own maintenance page. */
export const ALIDROP: Promo = {
  name: "AliDrop",
  headline: "Automate your AliExpress dropshipping journey with AliDrop",
  blurb:
    "Easily source the best AliExpress products, along with top selections from Alibaba and Temu, and trusted US and EU suppliers.",
  linkLabel: "Alidrop.co",
  linkUrl: "https://www.alidrop.co",
};

/** Identical on every frame — kept here so the ten copies can't drift. */
export const SHARED = {
  status: "Maintenance in progress",
  statusShort: "Maintenance",
  body: "We're running scheduled work on the platform. Nothing is lost, and your store keeps selling. We'll be back shortly.",
  cta: "Back to Home",
  reassurance: "This page refreshes itself when we're live.",
  promoEyebrow: "While you wait",
};

export const APP = {
  name: "AliDrop",
  /** `${name} will be back soon.` — the only per-app string in the headline. */
  home: "/",
  promo: DROPSHIPTOOL,

  /** Headline ink. Also the source for the divider and card border. */
  ink: "#191919",
  /** Body, reassurance, eyebrow, blurb, pill label. */
  muted: "#838383",

  /** Logo box, measured from this app's own frame. Rendered with
   *  object-fit: contain, so a wrong value letterboxes rather than distorts. */
  logo: {
    src: "/maintenance-logo.svg",
    width: 123,
    height: 28.89,
    mobileWidth: 94,
    mobileHeight: 22.08,
  },

  /** Ring left offsets as a percentage of frame width. AliDrop's frame puts
   *  them at 781px and 725px of 1440; Spocket and DropshipTool use 890/834. */
  rings: { outer: 54.2, inner: 50.3, outerMobile: 51.2, innerMobile: 36.8 },

  /** Background glow. Radii are the SVG matrix scale x10 (gradientUnits is
   *  userSpaceOnUse with r=10). Spocket's differ: 1728x808 at 82%. */
  glow: { radiusX: 1570, radiusY: 734, atX: 93, mobileRadiusX: 426 },

  /** Status pill border alpha. Two-digit hex suffix. 45 = 27%, 24 = 14%. */
  pillBorderAlpha: "45",

  /**
   * Typography is per-app, not shared. Nine of the ten use Inter; Spocket uses
   * Avenir, confirmed both in its Figma frame and in its own src/index.css.
   * Avenir's weight names map differently (Roman 400, Medium 500, Heavy 800,
   * Black 900), so a sibling on Avenir must override the weights too.
   */
  font: {
    family:
      '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    headline: 600,
    body: 400,
    cta: 700,
    reassurance: 300,
    eyebrow: 500,
    promoName: 600,
    promoHeadline: 600,
    promoBlurb: 400,
    promoLink: 600,
  },
};
